'use client';

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const BUCKET_KEY = 'postrack_admin_auth_bucket';

let browserClient: SupabaseClient | null = null;

/**
 * Read public Supabase env with **static** `process.env.NEXT_PUBLIC_*` access.
 * Next.js only inlines these for the browser when the key is a string literal;
 * `process.env[variable]` stays empty in client bundles.
 */
function getSupabasePublicEnv(): { url: string; anonKey: string } {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url) {
    throw new Error(
      'Missing NEXT_PUBLIC_SUPABASE_URL. Set it in .env and restart `next dev` (see POS-Track-Web / POS-Billing-System).',
    );
  }
  if (!anonKey) {
    throw new Error(
      'Missing NEXT_PUBLIC_SUPABASE_ANON_KEY. Set the publishable/anon key in .env and restart `next dev`.',
    );
  }
  return { url, anonKey };
}

function clearSupabaseKeys(storage: Storage) {
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k?.startsWith('sb-')) toRemove.push(k);
  }
  toRemove.forEach((k) => storage.removeItem(k));
}

/** Call before sign-in so the session is stored in localStorage (persistent) or sessionStorage (tab-only). */
export function configureAuthStorage(persistent: boolean) {
  if (typeof window === 'undefined') return;
  browserClient = null;
  if (persistent) {
    localStorage.setItem(BUCKET_KEY, 'persistent');
    sessionStorage.removeItem(BUCKET_KEY);
    clearSupabaseKeys(sessionStorage);
  } else {
    sessionStorage.setItem(BUCKET_KEY, 'session');
    localStorage.removeItem(BUCKET_KEY);
    clearSupabaseKeys(localStorage);
  }
}

function resolveAuthStorage(): Storage {
  if (typeof window === 'undefined') return localStorage;
  if (sessionStorage.getItem(BUCKET_KEY) === 'session') return sessionStorage;
  if (localStorage.getItem(BUCKET_KEY) === 'persistent') return localStorage;
  return localStorage;
}

export function getSupabaseBrowserClient(): SupabaseClient {
  if (typeof window === 'undefined') {
    throw new Error('getSupabaseBrowserClient is only available in the browser');
  }
  if (browserClient) return browserClient;
  const { url, anonKey } = getSupabasePublicEnv();
  browserClient = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: resolveAuthStorage(),
    },
  });
  return browserClient;
}
